import { useState, useEffect, useCallback } from 'react';

/**
 * Checks if the browser or document currently has an active fullscreen element.
 */
export function isFullscreenActive(): boolean {
  if (typeof document === 'undefined') return false;
  return Boolean(
    document.fullscreenElement ||
    (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement ||
    (document as unknown as { mozFullScreenElement?: Element }).mozFullScreenElement ||
    (document as unknown as { msFullscreenElement?: Element }).msFullscreenElement,
  );
}

/**
 * Checks if Fullscreen API is available in this browser environment.
 */
export function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const doc = document as unknown as {
    fullscreenEnabled?: boolean;
    webkitFullscreenEnabled?: boolean;
    mozFullScreenEnabled?: boolean;
  };
  const docEl = document.documentElement as unknown as {
    requestFullscreen?: unknown;
    webkitRequestFullscreen?: unknown;
    webkitRequestFullScreen?: unknown;
  };
  return Boolean(
    doc.fullscreenEnabled ||
    doc.webkitFullscreenEnabled ||
    doc.mozFullScreenEnabled ||
    docEl.requestFullscreen ||
    docEl.webkitRequestFullscreen ||
    docEl.webkitRequestFullScreen,
  );
}

export interface ToggleFullscreenResult {
  isFullscreen: boolean;
  attempted: boolean;
  iosTip?: boolean;
}

/**
 * Toggles fullscreen mode with mobile Safari & WebKit compatibility.
 */
export async function toggleFullscreen(): Promise<ToggleFullscreenResult> {
  if (typeof document === 'undefined') {
    return { isFullscreen: false, attempted: false };
  }

  // Scroll to collapse browser address bar on mobile devices
  if (typeof window !== 'undefined') {
    try {
      window.scrollTo(0, 1);
    } catch {
      /* ignore scroll errors */
    }
  }

  if (isFullscreenActive()) {
    try {
      const doc = document as unknown as {
        exitFullscreen?: () => Promise<void>;
        webkitExitFullscreen?: () => Promise<void>;
        mozCancelFullScreen?: () => Promise<void>;
      };
      if (typeof doc.exitFullscreen === 'function') {
        await doc.exitFullscreen();
      } else if (typeof doc.webkitExitFullscreen === 'function') {
        await doc.webkitExitFullscreen();
      } else if (typeof doc.mozCancelFullScreen === 'function') {
        await doc.mozCancelFullScreen();
      }
      return { isFullscreen: false, attempted: true };
    } catch {
      return { isFullscreen: false, attempted: true };
    }
  }

  const docEl = document.documentElement as unknown as {
    requestFullscreen?: (options?: { navigationUI?: string }) => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
    webkitRequestFullScreen?: () => Promise<void>;
  };

  try {
    if (typeof docEl.requestFullscreen === 'function') {
      await docEl.requestFullscreen({ navigationUI: 'hide' });
      return { isFullscreen: true, attempted: true };
    }
    if (typeof docEl.webkitRequestFullscreen === 'function') {
      await docEl.webkitRequestFullscreen();
      return { isFullscreen: true, attempted: true };
    }
    if (typeof docEl.webkitRequestFullScreen === 'function') {
      await docEl.webkitRequestFullScreen();
      return { isFullscreen: true, attempted: true };
    }
  } catch {
    // Mobile Safari on iPhone throws when requestFullscreen is called on documentElement
    // Suggest the Home Screen PWA path.
    return { isFullscreen: false, attempted: true, iosTip: true };
  }

  return { isFullscreen: false, attempted: true, iosTip: true };
}

/**
 * React hook for tracking and toggling browser fullscreen state.
 */
export function useFullscreen(): {
  isFullscreen: boolean;
  toggle: () => Promise<ToggleFullscreenResult>;
  supported: boolean;
  } {
  const [fullscreen, setFullscreen] = useState<boolean>(() => isFullscreenActive());
  const [supported] = useState<boolean>(() => isFullscreenSupported());

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(isFullscreenActive());
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggle = useCallback(async () => {
    const res = await toggleFullscreen();
    setFullscreen(isFullscreenActive());
    return res;
  }, []);

  return {
    isFullscreen: fullscreen,
    toggle,
    supported,
  };
}
